import { useCallback } from 'react';

import { faCopy } from '@fortawesome/pro-solid-svg-icons';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { Field } from 'core/forms';
import { Accordion, Button, Icon, Label } from 'core/ui';
import { DelimitedTextAutoComplete } from 'core/ui/DelimitedText';
import { hasText } from 'core/utils';
import { GridFilterManager } from 'core/utils/grid-filter-manager';

import { useApiClient, useApiQuery } from 'features/api';

import WriteIcon from '../assets/write.svg?react';
import { filterResultSetsForAutoComplete, resultSetTextFn } from '../services/ekg-reading-service';

const PreField = ({ value }) => {
  return <StyledPre>{value}</StyledPre>;
};

const StyledPre = styled.pre`
  font-family: inherit;
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
`;

PreField.propTypes = {
  value: PropTypes.string,
};

PreField.defaultProps = {
  value: undefined,
};

export const EKGResultsAccordion = ({ eventKey, valueGetter, valueSetter }) => {
  const apiClient = useApiClient();

  // Create proper data state for EKG result sets filter
  // Backend service already filters for Active == true, we just need to filter by service
  const dataState = GridFilterManager.prepareDataStateForAPI({
    filter: GridFilterManager.createEqualityFilters([
      { field: 'Service_Id', value: 1 }, // EKG service id - matches backend model field name
    ]),
    sort: [
      { field: 'acronym', dir: 'asc' },
      { field: 'description', dir: 'asc' },
    ],
  });

  // Use standard useApiQuery pattern
  const { data: overReadTemplates = [], isLoading } = useApiQuery({
    queryKey: ['resultSets', 'ekg'],
    apiFn: apiClient.resultSetClient.getAllResultSets,
    params: [dataState],
    staleTime: 1000 * 60 * 10, // Cache for 10 minutes since result sets don't change often
  });

  const handleCopyClick = useCallback(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const interpretation = valueGetter('interpretation');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const overread = valueGetter('overRead');

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    valueSetter('overRead', {
      value: `${hasText(overread) ? `${overread}\r\n` : ''}${hasText(interpretation) ? interpretation : ''}`,
    });
  }, [valueGetter, valueSetter]);

  return (
    <Accordion.Item eventKey={eventKey}>
      <button type="submit" disabled style={{ display: 'none' }} aria-hidden="true" />
      <Accordion.Header Icon={WriteIcon} title="Results" />
      <Accordion.Body>
        <StyledInterpretationLabel>
          Interpretation
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          {hasText(valueGetter('interpretation')) && (
            <StyledCopyButton type="button" title="Copy to Over-read" onClick={handleCopyClick}>
              <StyledCopyIcon icon={faCopy} />
            </StyledCopyButton>
          )}
        </StyledInterpretationLabel>
        <Field name="interpretation" component={PreField} readOnly />
        <Field
          name="overRead"
          component={DelimitedTextAutoComplete}
          label="Over-read"
          autoCompleteData={overReadTemplates}
          dataKeyField="id"
          valueField="description"
          textFn={resultSetTextFn}
          filterOptions={filterResultSetsForAutoComplete}
          disabled={isLoading}
        />
      </Accordion.Body>
    </Accordion.Item>
  );
};

EKGResultsAccordion.displayName = 'EKGResultsAccordion';

const StyledInterpretationLabel = styled(Label)`
  display: flex;
  align-items: end;
  justify-content: space-between;
`;

const StyledCopyButton = styled(Button)`
  margin-left: 16px;
  padding: 3px 12px;
`;

const StyledCopyIcon = styled(Icon)`
  &&& {
    margin-right: 0;
  }
`;

EKGResultsAccordion.propTypes = {
  eventKey: PropTypes.string.isRequired,
  valueGetter: PropTypes.func.isRequired,
  valueSetter: PropTypes.func.isRequired,
};
